import { StanButton } from '@/components';
import IcPlus from '@/assets/icons/ic-plus.svg';
import { cn, createIconElement } from '@/lib';

export const FUNNEL_PAGES_DATA_GRID = 'grid flex-1 grid-cols-1 items-center gap-4 md:grid-cols-[2fr_1fr_1fr_1fr]';

const FOLLOW_UP_SUGGESTION = {
    title: 'Following Offer',
    description: 'Create a page to send visitors after the subsequent offer',
} as const;

const SUGGESTED_PAGES = [
    { title: 'Entry Page', description: 'Create a page to welcome visitors and start your funnel' },
    { title: 'Subsequent Offer', description: 'Create a page with your main product/offering' },
    FOLLOW_UP_SUGGESTION,
] as const;

export function getSuggestedPageAt(index: number) {
    return SUGGESTED_PAGES[index] ?? FOLLOW_UP_SUGGESTION;
}

export const FunnelPagesEmptyValue = () => (
    <span className='text-caption-1-regular text-light max-md:hidden' aria-hidden>
        –
    </span>
);

// Reserves the exact right-edge width of a "Create Page" button so rows without
// a real action button stay aligned with rows that do have one.
export const FunnelPagesActionSlotSpacer = () => (
    <StanButton
        name='funnel-pages-action-spacer'
        variant='outline'
        size='sm'
        icon={IcPlus}
        onClick={() => {}}
        className='invisible h-0! shrink-0 max-md:hidden!'
        aria-hidden='true'
        tabIndex={-1}
    >
        Create Page
    </StanButton>
);

interface SectionHeaderProps {
    withActionSlot?: boolean;
}

export const FunnelPagesSectionHeader = ({ withActionSlot = false }: SectionHeaderProps) => (
    <div className='hidden items-center gap-4 md:flex'>
        <div className={FUNNEL_PAGES_DATA_GRID}>
            <span className='text-label-sm-bold text-gray'>Pages</span>
            <span className='text-label-sm-bold text-gray'>Sales</span>
            <span className='text-label-sm-bold text-gray'>Leads</span>
            <span className='text-label-sm-bold text-gray'>Views</span>
        </div>
        {withActionSlot && <FunnelPagesActionSlotSpacer />}
    </div>
);

// ----------------------------------------------------------------------------
// Shared suggested-page row (used by both the funnel list expanded view and
// the editor sidebar when a funnel has fewer than MIN_FUNNEL_ROWS real pages).
// ----------------------------------------------------------------------------

interface SuggestedPageRowProps {
    stepNumber: number;
    title: string;
    description: string;
    isLast: boolean;
    size?: 'sm' | 'md';
    layout?: 'inline' | 'grid';
    showCreateButton?: boolean;
    active?: boolean;
    onCreate: () => void;
}

const SUGGESTED_CHIP_SIZE = {
    sm: 'size-10',
    md: 'size-12',
} as const;

const SUGGESTED_TRUNK_TOP = {
    sm: 'top-12',
    md: 'top-14',
} as const;

const SUGGESTED_TRUNK_LEFT = {
    sm: 'left-5',
    md: 'left-6',
} as const;

export const SuggestedPageRow = ({
    stepNumber,
    title,
    description,
    isLast,
    size = 'md',
    layout = 'inline',
    showCreateButton = true,
    active = false,
    onCreate,
}: SuggestedPageRowProps) => {
    const isGrid = layout === 'grid';

    const nameBlock = (
        <div className='flex min-w-0 items-center gap-3'>
            <div className='bg-canvas relative z-[1] shrink-0'>
                <div
                    className={cn(
                        'text-brand-main border-brand-selected bg-canvas flex shrink-0 items-center justify-center rounded-md border border-dashed font-medium transition-all duration-200 ease-out',
                        SUGGESTED_CHIP_SIZE[size],
                        active
                            ? 'bg-brand-subtle border-brand-main ring-brand-selected opacity-100 ring-2'
                            : 'opacity-80 ring-0 ring-transparent'
                    )}
                >
                    {stepNumber}
                </div>
            </div>
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <span className='text-heading-h4 text-dark truncate'>{title}</span>
                <span className='text-body-sm text-gray truncate'>{description}</span>
            </div>
        </div>
    );

    // Stop propagation on the explicit "Create Page" button so it doesn't double-fire
    // the row-level click handler.
    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onCreate();
    };

    return (
        <div
            role='button'
            tabIndex={0}
            onClick={onCreate}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCreate();
                }
            }}
            aria-current={active ? 'step' : undefined}
            className='group relative flex w-full cursor-pointer items-center gap-4 py-2 text-left transition-transform duration-200 ease-out hover:translate-x-0.5'
        >
            {isGrid ? (
                <div className={FUNNEL_PAGES_DATA_GRID}>
                    {nameBlock}
                    <FunnelPagesEmptyValue />
                    <FunnelPagesEmptyValue />
                    <FunnelPagesEmptyValue />
                </div>
            ) : (
                <div className='min-w-0 flex-1'>{nameBlock}</div>
            )}

            {showCreateButton && (
                <>
                    <StanButton
                        name={`create-suggested-page-${stepNumber}`}
                        variant='outline'
                        size='sm'
                        icon={IcPlus}
                        onClick={handleButtonClick}
                        className='shrink-0 max-md:hidden!'
                    >
                        Create Page
                    </StanButton>
                    <span
                        role='presentation'
                        onClick={handleButtonClick}
                        className='border-brand-main text-brand-main hover:bg-brand-soft flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors md:hidden'
                    >
                        {createIconElement(IcPlus, { className: 'size-5 text-brand-main' })}
                    </span>
                </>
            )}

            {!isLast && (
                <div
                    className={cn(
                        'bg-brand-subtle absolute h-7 w-0.5',
                        SUGGESTED_TRUNK_TOP[size],
                        SUGGESTED_TRUNK_LEFT[size]
                    )}
                    aria-hidden
                />
            )}
        </div>
    );
};
